'use client';

/**
 * Abandoned-checkout recovery banner.
 *
 * Stripe's checkout `cancel_url` is `/pricing?checkout=cancelled` — a user who
 * bailed at Stripe is the highest-intent prospect we have, yet they previously
 * landed on the normal pricing page with no path back. This banner detects the
 * `checkout=cancelled` param and offers a one-click "Resume checkout" that
 * re-initiates the Stripe session exactly like BillingRequiredModal does
 * (resolve the plan → POST /api/checkout → window.location.assign(url)).
 *
 * Plan resolution: the checkout route's cancel_url carries the abandoned plan
 * (`&plan=<name>`), so we resume THAT plan when it resolves against the
 * pricing API. When it is not derivable (old links, tampered param) we fall
 * back to Spark and say so in the UI instead of silently switching plans.
 *
 * Dismissible and idempotent via sessionStorage so it never nags after the user
 * closes it. Accessible: role="region" + aria-label, focus moved to the heading
 * on mount, ≥44px tap targets.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { X, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

type BillingPlan = {
  plan_name: string;
  price_monthly: number;
  stripe_price_id: string;
};

const DISMISSED_FLAG = 'checkout_cancelled_recovery_dismissed_v1';

export function CheckoutCancelledRecovery() {
  const searchParams = useSearchParams();
  const cancelled = searchParams.get('checkout') === 'cancelled';
  // Plan the user actually abandoned — set by /api/checkout's cancel_url.
  const abandonedPlan = (searchParams.get('plan') ?? '').trim().toLowerCase() || null;

  // Lazy initializer reads the prior-dismissal flag at first render (client only;
  // returns false during SSR). Derived `visible` below means no synchronous
  // setState-in-effect is needed — visibility is a pure function of props/state.
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && sessionStorage.getItem(DISMISSED_FLAG) === '1',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const visible = cancelled && !dismissed;

  useEffect(() => {
    if (visible) headingRef.current?.focus();
  }, [visible]);

  function dismiss() {
    if (typeof window !== 'undefined') sessionStorage.setItem(DISMISSED_FLAG, '1');
    setDismissed(true);
  }

  async function resumeCheckout() {
    setLoading(true);
    setError(null);
    try {
      // Resolve the price the same way BillingRequiredModal does — the
      // pricing API is the SSOT for current price IDs. Prefer the plan the
      // user actually abandoned (cancel_url `plan` param); fall back to Spark.
      const pricingRes = await fetch('/api/pricing', { headers: { Accept: 'application/json' } });
      const pricingPayload = (await pricingRes.json().catch(() => null)) as { data?: BillingPlan[] } | null;
      const plans = Array.isArray(pricingPayload?.data) ? pricingPayload.data : [];
      const plan =
        (abandonedPlan
          ? plans.find((item) => item.plan_name.toLowerCase() === abandonedPlan)
          : undefined) ??
        plans.find((item) => item.plan_name.toLowerCase() === 'spark') ??
        plans[0];

      if (!plan?.stripe_price_id) {
        setError('Billing is not ready yet. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: plan.stripe_price_id }),
      });
      const payload = (await res.json().catch(() => null)) as
        | { success?: boolean; data?: { url?: string }; error?: string }
        | null;
      if (payload?.success && payload.data?.url) {
        window.location.assign(payload.data.url);
        return;
      }
      setError(payload?.error ?? 'Checkout session creation failed. Please try again.');
    } catch {
      setError('Could not open Stripe checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!visible) return null;

  return (
    <div className="mx-auto w-full max-w-3xl px-4">
      <div
        role="region"
        aria-label="Resume your checkout"
        className="relative rounded-2xl border border-cyan-300/25 bg-cyan-400/[0.06] p-5 shadow-[0_20px_70px_-32px_rgba(34,211,238,0.55)] sm:p-6"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-lg text-[var(--dimmer)] transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        <div className="pr-10">
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-lg font-semibold text-white outline-none sm:text-xl"
          >
            You were one step away — finish setting up your AI receptionist.
          </h2>
          <p className="mt-1 text-sm leading-5 text-[var(--dim)]">
            Your spot is still open. Pick up right where you left off and have your AI number
            provisioned in minutes.
          </p>
          {abandonedPlan ? (
            <p className="mt-1 text-xs text-[var(--dimmer)]">
              Resuming your <span className="capitalize">{abandonedPlan}</span> checkout.
            </p>
          ) : (
            <p className="mt-1 text-xs text-[var(--dimmer)]">
              We couldn&apos;t tell which plan you started with, so this resumes on Spark — pick a
              different plan below if that&apos;s not right.
            </p>
          )}
        </div>

        {error && (
          <p
            className="mt-4 rounded-lg border border-red-400/25 bg-red-500/10 px-3 py-2 text-sm text-red-100"
            role="alert"
          >
            {error}
          </p>
        )}

        <div className="mt-4">
          <Button
            onClick={() => {
              void resumeCheckout();
            }}
            loading={loading}
            disabled={loading}
            className="min-h-12 w-full gap-2 sm:w-auto"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
            {loading ? 'Opening billing...' : 'Resume checkout'}
          </Button>
        </div>
      </div>
    </div>
  );
}
